import React, { FC, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { 
    SpaceBackground, 
    NebulaEffect, 
    Wrapper, 
    GlassCard, 
    StyledLoginField, 
    StyledPasswordField, 
    StyledButton, 
    GlowingTitle, 
    SpaceLogo 
} from './styled';
import axios, { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedInput from '@mui/material/OutlinedInput';

const url = process.env.REACT_APP_API_URL + 'api/Authenticate/Login';
const getUserInfoURL = process.env.REACT_APP_API_URL + 'api/Authenticate/UserInfo';

const Authorization: FC<{ isAuth: (value: boolean) => void; getFioAndOrganization: (value: string) => void }> = ({
    isAuth,
    getFioAndOrganization,
}) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | {} | undefined>(undefined);
    const [autorizedData, setAutorizedData] = useState({
        login: '',
        password: '',
    });

    const handleLogin = (event: React.SyntheticEvent) => {
        const { value } = event.target as HTMLInputElement;
        setAutorizedData({ ...autorizedData, login: value.trim() });
    };

    const handlePassword = (event: React.SyntheticEvent) => {
        const { value } = event.target as HTMLInputElement;
        setAutorizedData({ ...autorizedData, password: value.trim() });
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.SyntheticEvent) => {
        event.preventDefault();
    };

    const handleSubmit = async () => {
        if (autorizedData.login && autorizedData.password) {
            setError(undefined);
            setIsLoading(true);

            try {
                await axios.post(url, autorizedData).then((data) => {
                    localStorage.setItem('token', data.data.token);
                    localStorage.setItem('token_time', data.data.expiration);

                    getUserInfo();
                    isAuth(true);
                });
            } catch (err) {
                if (err && err instanceof AxiosError) {
                    setError(err);
                    console.log('error: ', err);
                } else if (err && err instanceof Error) {
                    setError(err.message);
                    console.log('error: ', err);
                }
                setIsLoading(false);
            }
        }
    };

    const getUserInfo = () => {
        try {
            axios
                .get(getUserInfoURL, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                })
                .then((data) => {
                    const fio = data.data.surname + ' ' + data.data.name + ' ' + (data.data.patronymic || '');
                    const fioAndOrganization = `Авторизован: ${fio}. Организация: ${data.data.organizationName}`;

                    localStorage.setItem('user_fio', fio);
                    localStorage.setItem('fioAndOrganization', fioAndOrganization);

                    getFioAndOrganization(fioAndOrganization);
                    setIsLoading(false);
                    navigate('/');
                })
                .catch((err) => {
                    alert(`Не удалось загрузить данные о пользователе! ${err}`);
                    setIsLoading(false);
                });
        } catch (err) {
            alert(`Не удалось получить данные о пользователе! ${err}`);
            setIsLoading(false);
        }
    };

    const handleKeyDown = (event: any) => {
        if (event.code === 'Enter' || event.code === 'NumpadEnter') handleSubmit();
    };

    return (
        <SpaceBackground>
            <NebulaEffect />
            <Wrapper>
                {/* Логотип и заголовок */}
                <GlowingTitle>
                    {/* <SpaceLogo>🚀</SpaceLogo> */}
                    <Typography 
                        variant='h1'
                        sx={{
                            fontSize: '48px',
                            fontWeight: 'bold',
                            color: 'white',
                            margin: '0 0 16px 0',
                            textShadow: `
                                0 0 10px rgba(59, 130, 246, 0.5),
                                0 0 20px rgba(59, 130, 246, 0.3),
                                0 0 30px rgba(59, 130, 246, 0.2)
                            `,
                            animation: 'titleGlow 3s ease-in-out infinite',
                            '@keyframes titleGlow': {
                                '0%, 100%': {
                                    textShadow: `
                                        0 0 10px rgba(59, 130, 246, 0.5),
                                        0 0 20px rgba(59, 130, 246, 0.3),
                                        0 0 30px rgba(59, 130, 246, 0.2)
                                    `,
                                },
                                '50%': {
                                    textShadow: `
                                        0 0 15px rgba(59, 130, 246, 0.8),
                                        0 0 25px rgba(59, 130, 246, 0.5),
                                        0 0 35px rgba(59, 130, 246, 0.3)
                                    `,
                                },
                            },
                        }}
                    >
                        К
                        <IconButton 
                            href='https://rmiac18.ru/' 
                            target='_blank'
                            sx={{ 
                                padding: 0,
                                marginBottom: '10px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    transform: 'scale(1.1)',
                                    cursor: 'pointer',
                                }
                            }}
                        >
                            <img src='/logo-cosmos.svg' alt='custom icon' style={{ width: 50, height: 50 }} />
                        </IconButton>
                        СМОС
                    </Typography>
                    <Typography
                    style={{color: 'rgb(255, 255, 255)'}}>
                        КОмплексная Система Медицинского<br />
                        Обслуживания и Сопровождения
                    </Typography>
                </GlowingTitle>

                {/* Форма авторизации */}
                <GlassCard>
                    <StyledLoginField
                        variant='outlined'
                        label='Имя пользователя'
                        onChange={handleLogin}
                        onKeyDown={handleKeyDown}
                        error={Boolean(error)}
                        disabled={isLoading}
                        placeholder='Введите имя пользователя'
                    />
                    <StyledPasswordField variant='outlined' disabled={isLoading}>
                        <InputLabel htmlFor='outlined-adornment-password'>Пароль</InputLabel>
                        <OutlinedInput
                            id='outlined-adornment-password'
                            type={showPassword ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position='end'>
                                    <IconButton
                                        aria-label='toggle password visibility'
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge='end'
                                        sx={{ color: 'rgba(147, 197, 253, 0.8)' }}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label='Пароль'
                            onChange={handlePassword}
                            onKeyDown={handleKeyDown}
                            error={Boolean(error)}
                            placeholder='Введите пароль'
                        />
                    </StyledPasswordField>
                    
                    {error && (
                        <Typography 
                            mt={1} 
                            sx={{ 
                                color: 'rgba(239, 68, 68, 0.9)',
                                textAlign: 'center',
                                fontSize: '14px'
                            }}
                        >
                            Что-то пошло не так
                        </Typography>
                    )}
                    
                    {isLoading ? (
                        <Box mt={3} display='flex' justifyContent='center'>
                            <CircularProgress sx={{ color: 'rgba(59, 130, 246, 0.8)' }} />
                        </Box>
                    ) : (
                        <StyledButton 
                            variant='contained' 
                            size='large' 
                            type='submit' 
                            onClick={handleSubmit}
                            disabled={isLoading}
                        >
                            Войти в КОСМОС
                        </StyledButton>
                    )}
                </GlassCard>


                {/* Футер */}
                <Box mt={4} textAlign='center'>
                    <Typography sx={{ 
                        // color: 'rgba(147, 197, 253, 0.6)',
                        color: 'rgb(255, 255, 255)',
                        fontSize: '15px'
                    }}>
                        {/* © 2025 КОСМОС - Система будущего */}
                        {/* © 2025 КОмплексная Система Медицинского Обслуживания и Сопровождения */}
                        © 2025 Республиканский медицинский информационно-аналитический центр
                    </Typography>
                </Box>
            </Wrapper>
        </SpaceBackground>
    );
};

export default Authorization;
